/**
 * Flatten notification save validation payloads into a field map and message list.
 * Accepts V2 `{ field, message }[]`, a `{ field: message }` map, string arrays, and empty data.
 */

export const NOTIFICATION_ERROR_FIELDS = {
  NAME: ['name'],
  LOCATIONS: ['locations', 'location_key', 'location'],
  ROLES: ['roles'],
  OFFSET: [
    'taskDueDateOffsetWorkingDays',
    'earliestDueDateOffsetWorkingDays',
    'offset',
    'dueOffset',
  ],
  RECIPIENTS: ['toUsers', 'emails', 'emailAddress', 'recipients'],
  SCHEDULE: ['schedule', 'startTime', 'schedulerTiming', 'frequencyHours'],
  SUBJECT: ['subject'],
  BODY: ['mailBody', 'mail_body'],
  CATEGORY: ['category'],
  SLA: ['sla'],
  END_DAYS: ['endDays', 'endDate', 'end_days'],
};

const MESSAGE_FIELD_HINTS = [
  {
    pattern: /must be a whole number between -200 and 200/i,
    fields: NOTIFICATION_ERROR_FIELDS.OFFSET,
  },
  {
    pattern: /offset \(working days\) is required/i,
    fields: NOTIFICATION_ERROR_FIELDS.OFFSET,
  },
  {
    pattern: /specify a location and at least one user/i,
    fields: [...NOTIFICATION_ERROR_FIELDS.LOCATIONS, ...NOTIFICATION_ERROR_FIELDS.RECIPIENTS],
  },
  {
    pattern: /at least one role and one location/i,
    fields: [...NOTIFICATION_ERROR_FIELDS.LOCATIONS, ...NOTIFICATION_ERROR_FIELDS.ROLES],
  },
  {
    pattern: /select at least one location/i,
    fields: NOTIFICATION_ERROR_FIELDS.LOCATIONS,
  },
  {
    pattern: /select at least one role/i,
    fields: NOTIFICATION_ERROR_FIELDS.ROLES,
  },
  {
    pattern: /category is required/i,
    fields: NOTIFICATION_ERROR_FIELDS.CATEGORY,
  },
  {
    pattern: /valid schedule frequency/i,
    fields: NOTIFICATION_ERROR_FIELDS.SCHEDULE,
  },
];

function fieldErrorMessage(value) {
  if (Array.isArray(value)) return value.filter(Boolean).join(' ');
  if (value && typeof value === 'object') return String(value.message || '').trim();
  if (value == null) return '';
  return String(value).trim();
}

function isFieldMessageItem(item) {
  return Boolean(
    item
    && typeof item === 'object'
    && !Array.isArray(item)
    && typeof item.field === 'string'
    && Object.prototype.hasOwnProperty.call(item, 'message')
  );
}

function normalizeFieldName(field) {
  if (!field) return '';
  const text = String(field);
  const last = text.split('.').pop();
  return last || text;
}

function addMessage(messages, text) {
  if (!text) return;
  if (!messages.includes(text)) messages.push(text);
}

function inferFieldsFromMessage(message) {
  const text = String(message || '');
  for (let index = 0; index < MESSAGE_FIELD_HINTS.length; index += 1) {
    const hint = MESSAGE_FIELD_HINTS[index];
    if (hint.pattern.test(text)) return hint.fields;
  }
  return [];
}

function flattenRaw(raw, assign) {
  if (raw == null || raw === '') return;
  if (typeof raw === 'string') {
    assign('', raw);
    return;
  }
  if (Array.isArray(raw)) {
    raw.forEach((item) => flattenRaw(item, assign));
    return;
  }
  if (typeof raw !== 'object') return;
  if (isFieldMessageItem(raw)) {
    assign(raw.field, raw.message);
    return;
  }
  Object.keys(raw).forEach((key) => assign(key, raw[key]));
}

/**
 * @param {unknown} raw `err.response.data.data`
 * @param {string} [fallbackMessage] Envelope message when `data` is empty
 * @returns {{ byField: Object<string, string>, messages: string[] }}
 */
export function normalizeNotificationServerErrors(raw, fallbackMessage = '') {
  const byField = {};
  const messages = [];

  const assign = (field, message) => {
    const text = fieldErrorMessage(message);
    if (!text) return;
    addMessage(messages, text);
    const name = normalizeFieldName(field);
    if (name) byField[name] = text;
  };

  flattenRaw(raw, assign);

  if (!messages.length && fallbackMessage) {
    addMessage(messages, String(fallbackMessage).trim());
  }

  if (!Object.keys(byField).length) {
    messages.forEach((msg) => {
      inferFieldsFromMessage(msg).forEach((field) => {
        if (!byField[field]) byField[field] = msg;
      });
    });
  }

  return { byField, messages };
}

/**
 * @param {Object<string, unknown>|null|undefined} errors
 * @param {string|string[]} keys
 * @returns {string}
 */
export function readNotificationFieldError(errors, keys) {
  const source = errors && typeof errors === 'object' && !Array.isArray(errors) ? errors : {};
  const names = Array.isArray(keys) ? keys : [keys];
  for (let index = 0; index < names.length; index += 1) {
    const key = names[index];
    if (!Object.prototype.hasOwnProperty.call(source, key)) continue;
    const text = fieldErrorMessage(source[key]);
    if (text) return text;
  }
  return '';
}
